import React, { useContext, useEffect } from "react";

//** Context */
import { PaymentContext } from "../../../context/BraintreeContext.jsx";
import useNewBusiness from "../../../hooks/useNewBusiness.ts";
import usePreQualification from "../../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages.ts";

//** Page Components */
import { Button, ButtonStack, OneColumnStack, Section } from "@arp/react-ndbx";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Stack, Typography } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import Page from "../../../components/Page";

import { Application, TRACKIFY_EVENT } from "../../../config/const/enums";

import { paymentConfirmationDisclaimers } from "../../../config/disclaimers/disclaimers";

import useSharedData from "../../../hooks/useSharedData";
import { pdf } from "../../../utilities/axios-utils.js";
import { addToYears, formatDate } from "../../../utilities/dateFormat.js";
import PageLoadingBackdrop from "../../../components/organisms/PageLoadingBackdrop";
import { backToQuotePoint } from "../../../utilities/utils.js";
//** Utilities */
import { formatCurrency } from "../../../utilities/numericFormat.js";
import { pushEvent, trackifyPageChange } from "@allianz/tracking";
import AdditionalOwnerDisclaimerAccordion from "../../../components/pageLevel/sharedComponents/AdditionalOwnerDisclaimerAccordion.jsx";

//----------------------------------------------------------------

export default function PolicyPurchaseConfirmation() {
  const { state } = usePreQualificationPages();
  const { currentPage } = { ...state };

  const paymentContext = useContext(PaymentContext);
  const { isPaymentMade, transactionData, paymentMethod } = paymentContext;

  const { data } = useNewBusiness();
  const { quoteInformation } = { ...data };
  const { policyPremiumsTotal } = { ...quoteInformation };

  const { accessToken, handleEventChange } = useSharedData();

  //* Page variables.
  const { dataSet } = usePreQualification();
  const { policyDocumentRequestType, paymentFrequencyUsed, personOne } = {
    ...dataSet,
  };
  const policyType = "Allianz Life Plan";
  const anniversaryDate = formatDate(addToYears(new Date(), 1));
  const isOnline = state.application === Application.ONLINE ? true : false;

  const downloadWelcomePack = async () => {
    handleEventChange({ name: "isPageLoading", value: true });
    const url = await pdf({
      path: "/documents/welcomePack",
      accessToken,
    });
    handleEventChange({ name: "isPageLoading", value: false });
    if (url) {
      window.open(url, "_blank");
    }
    return false;
  };

  useEffect(() => {
    pushEvent(
      "paymentMethod",
      paymentMethod === "CC"
        ? TRACKIFY_EVENT.CREDIT_CARD
        : TRACKIFY_EVENT.BANK_ACCOUNT
    );
    trackifyPageChange(currentPage?.identifier?.toLowerCase());
  }, []);

  const theme = useTheme();

  return isPaymentMade ? (
    <>
      <PageLoadingBackdrop />
      <OneColumnStack mt={4} mb={4} sx={{ justifyContent: "center" }}>
        <Section>
          <Typography variant="h1" sx={{ mb: 1 }}>
            Confirmation
          </Typography>
          <Typography variant="p3">
            Hi {personOne?.firstName}, your progress is now complete
          </Typography>
        </Section>
      </OneColumnStack>
      <Section
        pb={8}
        sx={{
          minWidth: `calc(100vw -  50px)`,
          backgroundColor: theme.palette.soft.softBlue150,
        }}
      >
        <Section>
          <CheckCircleRoundedIcon
            sx={{
              fontSize: 88,
              color: `${theme.palette.signal.Success500}`,
              mb: 2,
              mt: 6,
            }}
          />
        </Section>
        <Section xs={8}>
          <Typography variant="h3">
            Thank you {personOne?.firstName} for choosing to insure with
            Allianz!
          </Typography>
        </Section>
        <Section sx={{ mt: 5, mb: { xs: 3, sm: 4 } }}>
          <Section sx={{ maxWidth: { xs: 334 }, mb: 0 }}>
            <img
              width="100%"
              src="https://www.allianzretireplus.com.au/content/dam/onemarketing/azau/allianz-life-dam/images/woman_laptop_headset.svg"
            ></img>
          </Section>
        </Section>
        <Section xs={6} lg={6} xl={4} sx={{ outline: "0px solid red" }}>
          <Typography variant="h5">
            Your application process is now completed and we will send your
            policy information to you shortly.
          </Typography>
        </Section>
      </Section>
      <Page title={currentPage.title} identifier={currentPage.identifier}>
        <Stack
          mt={4}
          mb={8}
          sx={{
            maxWidth: 642,
            outline: "0px solid red",
            alignItems: "flex-start",
          }}
        >
          <Section sx={{ alignItems: "flex-start", mb: 4 }}>
            <Typography variant="h3-bold">Policy details</Typography>
          </Section>
          <Stack direction="row">
            <Stack
              direction="column"
              spacing={1}
              alignItems="flex-start"
              mr={4}
              sx={{ outline: "0px solid red" }}
            >
              <Typography variant="p2" sx={{ fontSize: 18 }}>
                Policy number:
              </Typography>
              <Typography variant="p2" sx={{ fontSize: 18 }}>
                Product type:
              </Typography>
              <Typography variant="p2" sx={{ fontSize: 18 }}>
                Anniversary date:
              </Typography>
              <Typography variant="p2" sx={{ fontSize: 18 }}>
                {paymentFrequencyUsed === "monthly"
                  ? "Total monthly premium:"
                  : "Total yearly premium:"}
              </Typography>
            </Stack>
            <Stack
              direction="column"
              spacing={1}
              alignItems="flex-start"
              sx={{ outline: "0px solid green" }}
            >
              <Typography variant="p2-bold" sx={{ fontSize: 18 }}>
                {transactionData?.header?.contract?.policyNumber || "Not found"}
              </Typography>
              <Typography variant="p2-bold" sx={{ fontSize: 18 }}>
                {policyType}
              </Typography>
              <Typography variant="p2-bold" sx={{ fontSize: 18 }}>
                {anniversaryDate}
              </Typography>
              <Typography variant="p2-bold" sx={{ fontSize: 18 }}>
                {policyPremiumsTotal
                  ? formatCurrency(policyPremiumsTotal[paymentFrequencyUsed])
                  : "Not found"}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Section mb={4} xs={10}>
          <AdditionalOwnerDisclaimerAccordion />
        </Section>
        <Section mb={4} xs={10}>
          <hr />
        </Section>

        <Stack
          mb={8}
          mt={8}
          sx={{
            maxWidth: 642,
            outline: "0px solid red",
            alignItems: "flex-start",
          }}
        >
          <Typography variant="h3-bold" mb={4}>
            {
              paymentConfirmationDisclaimers({
                isOnline: true,
              }).welcomePackInfo[0].WELCOME_PACK.title
            }
          </Typography>
          <Section
            mb={{ xs: 2, md: 3 }}
            sx={{ lineHeight: 0, textAlign: "justify" }}
          >
            <Typography variant="p2">
              {paymentConfirmationDisclaimers.note}
              {
                paymentConfirmationDisclaimers({
                  isOnline: isOnline,
                  deliveryType:
                    policyDocumentRequestType === "yes" ? "email" : "post",
                }).welcomePackInfo[0].WELCOME_PACK.note
              }
            </Typography>
          </Section>
        </Stack>

        <Section mt={9} md={10} mb={12}>
          <ButtonStack>
            {isOnline ? (
              <Button
                variant="contained"
                color="secondary"
                type="button"
                size="medium"
                onClick={() =>
                  (window.location.href = `https://www.allianz.com.au/`)
                }
              >
                Continue to Allianz Website
              </Button>
            ) : (
              <Button
                id="cc-processing-continue"
                ariaLabel="continue"
                variant="contained"
                color="secondary"
                type="button"
                size="medium"
                onClick={() => backToQuotePoint(Application.CONTACT_CENTER)}
              >
                Return to Search
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              type="button"
              size="medium"
              href="javascript:;"
              onClick={downloadWelcomePack}
            >
              Download your Welcome pack
            </Button>
          </ButtonStack>
        </Section>
      </Page>{" "}
    </>
  ) : null;
}
